import type { RecoverRequest } from '../api'

/**
 * The decisions behind the assignment queue's three new views, kept out of the components so they
 * can be reasoned about and tested rather than re-derived per screen.
 *
 * The house rule these all serve: THE SCREEN RANKS AND STATES, IT NEVER ASSIGNS. Every function
 * here produces an ordering or a description; none of them picks.
 */

export interface Engineer {
  id: number
  name: string | null
  locationId: number | null
  location: string | null
  primaryTeamId: number | null
  primaryTeam: string | null
  otherTeams: string[]
  assignmentsTotal: number
  assignmentsWorking: number
  /** Which objects they hold, so the rail can name them rather than only counting them. */
  assignmentObjects?: { objectId: string; working: boolean }[]
}

export interface WorkforceSite {
  locationId: number
  location: string | null
  go: number
  wait: number
  teamsInDemand: string[]
  engineerIds: number[]
  nearestCover: { locationId: number; location: string | null; distanceKm: number; engineers: number } | null
}

export interface Workforce {
  /**
   * Access read from each object's RUNBOOK, keyed by object id. Absent for objects with no
   * runbook, where the caller falls back to the name and marks the answer as inferred.
   */
  accessByObject?: Record<string, { mode: AccessRequirement; source: 'classification' | 'runbook' }>
  engineers: Engineer[]
  sites: WorkforceSite[]
  unavailableFields: { field: string; reason: string }[]
}

export type PresenceState = 'available' | 'busy' | 'away' | 'offline'

/* ── Access ──────────────────────────────────────────────────────────────────────────────── */

export type AccessRequirement = 'on-site' | 'remote'

/**
 * PROPOSED FIELD, DERIVED IN THE MEANTIME.
 *
 * Nothing on an object records whether recovering it needs somebody standing in front of it. The
 * runbooks say so in prose — "physical access to the switch", "console cable from the laptop" —
 * so until there is a real flag this reads the words we do have.
 *
 * Deliberately conservative: it only says on-site when the evidence is a physical-access noun, and
 * defaults to remote otherwise. Being wrong towards on-site shrinks the candidate pool for work
 * anyone could have taken, which is the more expensive mistake during a live recovery.
 */
const ON_SITE_EVIDENCE = [
  'console cable', 'console port', 'physical access', 'terminal emulation',
  'serial', 'rack', 'kvm', 'usb', 'front panel', 'power on the',
]

/**
 * THE RUNBOOK ANSWERS THIS; THE OBJECT'S NAME ONLY GUESSES AT IT.
 *
 * THREE SOURCES, IN ORDER OF STANDING.
 *
 * classification — Classifications.AccessMode, a decision made once about what this KIND of thing
 *   is. A Core Network Switch is recovered at the rack because of what it is. Not a guess.
 * runbook — the object's own steps mention physical access or a console cable. Only answers for
 *   objects that have a runbook, and only until their classification is decided.
 * name — the last resort, matching words in the description. This was the original implementation
 *   and it is why a dark fibre and an exchange circuit came out "Remote" while the switches beside
 *   them were classified from the same weak signal.
 *
 * Only the first is authoritative; the other two mark themselves as inferred.
 */
export function accessRequirement(
  row: { objectDescription?: string | null; objectID?: string | null },
  known?: Record<string, { mode: AccessRequirement; source: 'classification' | 'runbook' }> | null,
): { access: AccessRequirement; inferred: boolean; source: 'classification' | 'runbook' | 'name' } {
  const answer = row.objectID ? known?.[row.objectID] : undefined
  // A classification is a decision somebody made about what this KIND of thing is; a runbook
  // reading is one object's prose. Both beat the name, only the first is not a guess.
  if (answer) return { access: answer.mode, inferred: answer.source !== 'classification', source: answer.source }

  const text = `${row.objectDescription ?? ''} ${row.objectID ?? ''}`.toLowerCase()
  if (ON_SITE_EVIDENCE.some((k) => text.includes(k))) return { access: 'on-site', inferred: true, source: 'name' }
  // Network hardware is recovered at the rack whatever the description says. Matched as a
  // substring, not on word boundaries: the strongest evidence is often the object ID itself
  // (MNSwitch1, SFF5LoadBalancer1) where the noun is welded to a site prefix and a number.
  if (['switch', 'firewall', 'router', 'loadbalancer', 'load balancer', 'appliance']
        .some((k) => text.includes(k))) return { access: 'on-site', inferred: true, source: 'name' }
  return { access: 'remote', inferred: true, source: 'name' }
}

/* ── Dependency order ────────────────────────────────────────────────────────────────────── */

/**
 * Network foundation first, then up the stack.
 *
 * LayerID on RequestsAssign already carries the CLASSIFICATION band — lowest band is network and
 * storage, rising to business services — so the recovery order is a property of the data rather
 * than a list of names to keep in step with the estate. Ties break on the committed schedule
 * position, which is the next most meaningful thing.
 */
export function byDependencyOrder(a: RecoverRequest, b: RecoverRequest): number {
  const band = (r: RecoverRequest) => (typeof r.layerID === 'number' ? r.layerID : Number.MAX_SAFE_INTEGER)
  const d = band(a) - band(b)
  if (d !== 0) return d
  const t = (r: RecoverRequest) => (r.forecastStartTime ? new Date(r.forecastStartTime).getTime() : Number.MAX_SAFE_INTEGER)
  return t(a) - t(b)
}

/* ── Candidate ranking ───────────────────────────────────────────────────────────────────── */

export interface Candidate {
  engineer: Engineer
  presence: PresenceState
  /** Same site as the object. */
  onSite: boolean
  /** Primary team matches the object's responsible team. */
  primaryTeamMatch: boolean
  /** The team is one of their other teams, not their primary. */
  secondaryTeamMatch: boolean
  /** Straight-line km from the engineer's site to the object's, when both have coordinates. */
  distanceKm: number | null
  /** Excluded because the object needs physical presence and they are elsewhere. */
  ineligible: boolean
  pro: string
  con: string
}

const PRESENCE_RANK: Record<PresenceState, number> = { available: 0, busy: 1, away: 2, offline: 3 }

/**
 * Site match, then primary team, then availability, then current load — in that order, because
 * that is the order in which being wrong costs the most. An engineer in the wrong city cannot take
 * on-site work at all; one in the wrong team can usually still do it.
 *
 * Returns EVERY candidate, ineligible ones included and marked. Silently dropping them would leave
 * command and control unable to see that the pool is empty for a reason.
 */
export function rankCandidates(opts: {
  engineers: Engineer[]
  presenceOf: (id: number) => PresenceState
  objectLocationId: number | null
  objectTeamId: number | null
  access: AccessRequirement
  runbookMinutes?: number | null
  siteEngineerCount?: (locationId: number | null) => number
  distanceKm?: (fromLocationId: number | null) => number | null
}): Candidate[] {
  const { engineers, presenceOf, objectLocationId, objectTeamId, access } = opts

  const shaped: Candidate[] = engineers.map((e) => {
    const presence = presenceOf(e.id)
    const onSite = objectLocationId != null && e.locationId === objectLocationId
    const primaryTeamMatch = objectTeamId != null && e.primaryTeamId === objectTeamId
    const secondaryTeamMatch = !primaryTeamMatch && objectTeamId != null
      && e.otherTeams.length > 0 && e.primaryTeamId !== objectTeamId
    const distanceKm = opts.distanceKm?.(e.locationId) ?? null
    const ineligible = access === 'on-site' && !onSite

    return {
      engineer: e, presence, onSite, primaryTeamMatch, secondaryTeamMatch, distanceKm, ineligible,
      pro: proLine({ e, presence, onSite, primaryTeamMatch, secondaryTeamMatch }),
      con: conLine({
        e, presence, onSite, primaryTeamMatch, distanceKm, ineligible, access,
        objectTeamId,
        runbookMinutes: opts.runbookMinutes ?? null,
        siteEngineers: opts.siteEngineerCount?.(e.locationId) ?? 0,
      }),
    }
  })

  return shaped.sort((a, b) => {
    if (a.ineligible !== b.ineligible) return a.ineligible ? 1 : -1
    if (a.onSite !== b.onSite) return a.onSite ? -1 : 1
    if (a.primaryTeamMatch !== b.primaryTeamMatch) return a.primaryTeamMatch ? -1 : 1
    const p = PRESENCE_RANK[a.presence] - PRESENCE_RANK[b.presence]
    if (p !== 0) return p
    const load = a.engineer.assignmentsTotal - b.engineer.assignmentsTotal
    if (load !== 0) return load
    return String(a.engineer.name ?? '').localeCompare(String(b.engineer.name ?? ''))
  })
}

function proLine(c: { e: Engineer; presence: PresenceState; onSite: boolean; primaryTeamMatch: boolean; secondaryTeamMatch: boolean }): string {
  if (c.onSite && c.primaryTeamMatch) return `On site and on the right team`
  if (c.onSite) return `Already at ${c.e.location ?? 'the site'}`
  if (c.primaryTeamMatch) return `${c.e.primaryTeam} — the team this object wants`
  if (c.secondaryTeamMatch) return `Carries ${c.e.otherTeams.join(', ')} as a second skill`
  if (c.presence === 'available' && c.e.assignmentsTotal === 0) return 'Free — nothing assigned'
  return 'Available to take work'
}

function conLine(c: {
  e: Engineer; presence: PresenceState; onSite: boolean; primaryTeamMatch: boolean
  distanceKm: number | null; ineligible: boolean; access: AccessRequirement
  objectTeamId: number | null; runbookMinutes: number | null; siteEngineers: number
}): string {
  if (c.ineligible) {
    return c.distanceKm != null
      ? `Needs physical access — ${c.e.location ?? 'elsewhere'} is ${c.distanceKm} km away`
      : `Needs physical access — not at this site`
  }
  if (c.presence === 'offline') return 'Not logged in'
  if (c.presence === 'away') return 'Away — would need calling back'
  if (c.e.assignmentsWorking > 0) return `Mid-recovery on ${c.e.assignmentsWorking} object${c.e.assignmentsWorking === 1 ? '' : 's'}`
  if (c.e.assignmentsTotal > 0) return `${c.e.assignmentsTotal} already queued and not started`
  // The cost nobody sees until it bites: taking the only engineer off a site.
  if (c.onSite && c.siteEngineers === 1) {
    const mins = c.runbookMinutes
    return mins
      ? `Only engineer at ${c.e.location ?? 'this site'} — site loses cover for ~${mins} min`
      : `Only engineer at ${c.e.location ?? 'this site'} — site loses cover`
  }
  if (c.objectTeamId != null && !c.primaryTeamMatch) {
    return `${c.e.primaryTeam ?? 'Their team'}, not the team this object wants`
  }
  if (c.objectTeamId == null) return 'Object has no responsible team recorded'
  return 'No obvious drawback'
}


/**
 * The workforce headline: how many are here, how many are on something, how many are free.
 *
 * Lives here rather than in the view because the Resource screen no longer draws its own row of
 * cards — the page's single contextual row draws them, and two components computing the same four
 * numbers from the same inputs is how they end up disagreeing on screen.
 */
export function workforceTotals(
  engineers: Engineer[],
  stateOf: (id: number) => 'available' | 'busy' | 'away' | 'offline',
): { loggedIn: number; working: number; available: number; away: number } {
  let loggedIn = 0, working = 0, available = 0, away = 0
  for (const e of engineers) {
    const s = stateOf(e.id)
    if (s !== 'offline') loggedIn += 1
    if (e.assignmentsWorking > 0) working += 1
    else if (s === 'available') available += 1
    if (s === 'away') away += 1
  }
  return { loggedIn, working, available, away }
}
